import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripeTerminal } from '@stripe/terminal-js';
import { supabase } from '../../../lib/supabaseClient';
import { edgeFunctionUrl, EdgeFunctions } from '../../../lib/edgeFunctions';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface TipModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: string;
  serviceTotal: number;
  staffName: string;
  onSuccess: (tipData: { amount: number; tip_percentage: number | null }) => void;
  paymentMethodId?: string | null; // For restaurant-style auto-charge
  paymentSource?: string | null; // 'terminal' or 'checkout'
  clientWalletBalance?: number | null; // Client's wallet balance for wallet tip payment
  clientHasSavedCard?: boolean; // Whether client has a saved card (from topup/membership)
}

// Payment option type
type PaymentOption = 'card' | 'wallet' | 'new_card' | 'terminal';

// Payment form component (Step 2)
const TipPaymentForm: React.FC<{
  appointmentId: string;
  tipPercentage: number | null;
  tipAmount: number;
  staffName: string;
  onSuccess: (tipData: { amount: number; tip_percentage: number | null }) => void;
  onBack: () => void;
}> = ({ appointmentId, tipPercentage, tipAmount, staffName, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Confirm payment
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href, // Not used, but required
        },
        redirect: 'if_required', // Stay on page
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Confirm payment on backend
        const { data: { session } } = await supabase.auth.getSession();

        const response = await fetch(edgeFunctionUrl(EdgeFunctions.CONFIRM_TIP_PAYMENT), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || ''}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          },
          body: JSON.stringify({
            payment_intent_id: paymentIntent.id,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to confirm tip payment');
        }

        // Success! Pass tip data to parent
        onSuccess({ amount: tipAmount, tip_percentage: tipPercentage });
      } else {
        throw new Error('Payment was not successful');
      }
    } catch (error: any) {
      console.error('Tip payment error:', error);
      setErrorMessage(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Complete Tip Payment</h3>
        <p className="text-gray-600 mt-1">Tip for {staffName}</p>
        <p className="text-2xl font-bold text-green-600 mt-2">
          ${tipAmount.toFixed(2)}
          {tipPercentage && <span className="text-sm text-gray-500 ml-2">({tipPercentage}%)</span>}
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <PaymentElement />
      </div>

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={isProcessing}
          className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Processing...
            </span>
          ) : (
            'Confirm Tip'
          )}
        </button>
      </div>
    </form>
  );
};

// Main TipModal component
const TipModal: React.FC<TipModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  serviceTotal,
  staffName,
  onSuccess,
  paymentMethodId,
  paymentSource,
  clientWalletBalance,
  clientHasSavedCard,
}) => {
  const [step, setStep] = useState<'select' | 'payment' | 'success'>('select');
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(null);
  const [customPercentage, setCustomPercentage] = useState<string>('');
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<{ amount: number; tip_percentage: number | null } | null>(null);

  // Payment option state
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOption>('card');

  // Terminal payment states
  const [isProcessingTerminal, setIsProcessingTerminal] = useState(false);
  const [terminalStatus, setTerminalStatus] = useState<string>('');
  const [terminalError, setTerminalError] = useState<string | null>(null);
  const [canCancelTerminal, setCanCancelTerminal] = useState(false);
  const terminalRef = useRef<any>(null);
  const discoveredReadersRef = useRef<any[]>([]);
  const paymentIntentRef = useRef<any>(null);

  // Derived state from props
  const hasSavedCard = !!paymentMethodId || !!clientHasSavedCard;
  const walletBalance = clientWalletBalance;

  // Preset percentages
  const presets = [15, 18, 20, 25];

  // Calculate tip amount when percentage changes
  useEffect(() => {
    if (selectedPercentage !== null) {
      const amount = (serviceTotal * selectedPercentage) / 100;
      setTipAmount(Math.round(amount * 100) / 100);
    }
  }, [selectedPercentage, serviceTotal]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedPercentage(null);
      setCustomPercentage('');
      setTipAmount(0);
      setClientSecret(null);
      setErrorMessage(null);
      setIsCreating(false);
      setSuccessData(null);
      // Reset terminal states
      setIsProcessingTerminal(false);
      setTerminalStatus('');
      setTerminalError(null);
      setCanCancelTerminal(false);
      // Default to saved card if available, otherwise new card
      setSelectedPaymentOption(hasSavedCard ? 'card' : 'new_card');
    }
  }, [isOpen, hasSavedCard]);

  // Initialize Stripe Terminal
  useEffect(() => {
    const initializeTerminal = async () => {
      if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
        console.error('Stripe publishable key not found');
        return;
      }

      try {
        const StripeTerminal = await loadStripeTerminal();

        if (!StripeTerminal) {
          console.error('Failed to load Stripe Terminal');
          return;
        }

        terminalRef.current = StripeTerminal.create({
          onFetchConnectionToken: async () => {
            // Fetch connection token from our edge function
            const { data: { session } } = await supabase.auth.getSession();

            const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_CONNECTION_TOKEN), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token || ''}`,
                'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
              },
            });

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || 'Failed to fetch connection token');
            }

            return data.secret;
          },
          onUnexpectedReaderDisconnect: () => {
            setTerminalStatus('Reader disconnected');
          },
        });
      } catch (error) {
        console.error('Terminal initialization failed:', error);
      }
    };

    if (isOpen) {
      initializeTerminal();
    }

    return () => {
      // Cleanup: safely disconnect reader when modal closes
      const cleanup = async () => {
        if (terminalRef.current) {
          try {
            const status = terminalRef.current.getConnectionStatus();
            if (status === 'connected') {
              await terminalRef.current.disconnectReader();
            }
          } catch (err) {
            // Silent cleanup
          }
        }
      };
      cleanup();
    };
  }, [isOpen]);

  const handlePresetClick = (percentage: number) => {
    setSelectedPercentage(percentage);
    setCustomPercentage('');
  };

  const handleCustomChange = (value: string) => {
    setCustomPercentage(value);
    const percentage = parseFloat(value);
    if (!isNaN(percentage) && percentage > 0) {
      setSelectedPercentage(percentage);
    } else {
      setSelectedPercentage(null);
      setTipAmount(0);
    }
  };

  const handleContinue = async () => {
    if (selectedPercentage === null || tipAmount <= 0) {
      setErrorMessage('Please select a tip amount');
      return;
    }

    // Validate wallet payment option
    if (selectedPaymentOption === 'wallet') {
      if (walletBalance === null || walletBalance < tipAmount) {
        setErrorMessage(`Insufficient wallet balance. Available: $${(walletBalance || 0).toFixed(2)}`);
        return;
      }
    }

    setIsCreating(true);
    setErrorMessage(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Determine payment method
      const payWithWallet = selectedPaymentOption === 'wallet';
      const shouldAutoCharge = selectedPaymentOption === 'card' && (!!paymentMethodId || hasSavedCard);

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_TIP_PAYMENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          appointment_id: appointmentId,
          tip_percentage: selectedPercentage,
          auto_charge: shouldAutoCharge,
          pay_with_wallet: payWithWallet,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create tip payment');
      }

      // If paid with wallet or auto-charged successfully, close modal immediately
      if ((data.paidWithWallet || data.autoCharged) && data.paymentStatus === 'succeeded') {
        handlePaymentSuccess({ amount: tipAmount, tip_percentage: selectedPercentage });
      } else if (data.clientSecret) {
        // Show payment form (only if we have a client secret)
        setClientSecret(data.clientSecret);
        setStep('payment');
      } else {
        // No client secret and not auto-charged - something went wrong
        throw new Error('Payment could not be processed. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating tip payment:', error);
      setErrorMessage(error.message || 'Failed to create payment. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handlePaymentSuccess = (tipData: { amount: number; tip_percentage: number | null }) => {
    // Show success confirmation screen instead of immediately closing
    setSuccessData(tipData);
    setStep('success');
    setTerminalStatus(''); // Clear terminal status
  };

  // Called when user clicks "Done" on success screen
  const handleSuccessDone = () => {
    if (successData) {
      onSuccess(successData);
    }
    onClose();
  };

  // Handle cancel terminal payment
  const handleCancelTerminalPayment = async () => {
    try {
      setTerminalStatus('Cancelling...');

      // Cancel the payment collection if in progress
      if (paymentIntentRef.current) {
        await terminalRef.current?.cancelCollectPaymentMethod();
      }

      // Disconnect reader
      if (terminalRef.current) {
        const status = terminalRef.current.getConnectionStatus();
        if (status === 'connected') {
          await terminalRef.current.disconnectReader();
        }
      }

      // Reset states
      setTerminalStatus('');
      setTerminalError(null);
      setIsProcessingTerminal(false);
      setCanCancelTerminal(false);
      paymentIntentRef.current = null;
    } catch (error: any) {
      setTerminalError('Failed to cancel payment. Please try closing the modal.');
      setIsProcessingTerminal(false);
      setCanCancelTerminal(false);
    }
  };

  // Handle terminal tip payment
  const handleTerminalPayment = async () => {
    if (selectedPercentage === null || tipAmount <= 0) {
      setErrorMessage('Please select a tip amount');
      return;
    }

    setIsProcessingTerminal(true);
    setTerminalError(null);
    setTerminalStatus('Connecting to reader...');
    setCanCancelTerminal(false);

    try {
      if (!terminalRef.current) {
        throw new Error('Terminal not initialized');
      }

      if (!process.env.REACT_APP_STRIPE_LOCATION_ID) {
        throw new Error('Stripe Location ID not configured');
      }

      // Step 1: Discover readers
      setTerminalStatus('Discovering readers...');

      const isTestMode = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.includes('test') || false;
      const discoverConfig = isTestMode
        ? { simulated: true }
        : { location: process.env.REACT_APP_STRIPE_LOCATION_ID };

      const discoverResult = await terminalRef.current.discoverReaders(discoverConfig);

      if (discoverResult.error) {
        throw new Error(`Failed to discover readers: ${discoverResult.error.message}`);
      }

      if (discoverResult.discoveredReaders.length === 0) {
        const errorMsg = isTestMode
          ? 'No simulated readers found. Try refreshing the page.'
          : 'No readers found. Make sure your S700 is online and registered.';
        throw new Error(errorMsg);
      }

      discoveredReadersRef.current = discoverResult.discoveredReaders;

      // Step 2: Connect to first available reader
      const readerLabel = discoveredReadersRef.current[0].label || 'reader';
      const readerType = isTestMode ? ' (simulated)' : '';
      setTerminalStatus(`Connecting to ${readerLabel}${readerType}...`);
      const connectResult = await terminalRef.current.connectReader(discoveredReadersRef.current[0]);

      if (connectResult.error) {
        throw new Error(`Failed to connect to reader: ${connectResult.error.message}`);
      }

      // Step 3: Create Terminal Tip PaymentIntent via edge function
      setTerminalStatus('Creating tip payment...');
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_TERMINAL_TIP_PAYMENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          appointment_id: appointmentId,
          tip_percentage: selectedPercentage,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create tip payment');
      }

      paymentIntentRef.current = data.clientSecret;

      // Step 4: Collect payment on reader
      setTerminalStatus('Present card on reader...');
      setCanCancelTerminal(true);

      const collectResult = await terminalRef.current.collectPaymentMethod(data.clientSecret);

      if (collectResult.error) {
        if (collectResult.error.code === 'canceled') {
          throw new Error('Payment cancelled by user');
        }
        throw new Error(`Payment collection failed: ${collectResult.error.message}`);
      }

      setCanCancelTerminal(false);

      // Step 5: Process payment
      setTerminalStatus('Processing tip payment...');
      const processResult = await terminalRef.current.processPayment(collectResult.paymentIntent);

      if (processResult.error) {
        throw new Error(`Payment processing failed: ${processResult.error.message}`);
      }

      // Step 6: Confirm tip payment on backend
      const confirmResponse = await fetch(edgeFunctionUrl(EdgeFunctions.CONFIRM_TIP_PAYMENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          payment_intent_id: processResult.paymentIntent.id,
        }),
      });

      const confirmData = await confirmResponse.json();

      if (!confirmResponse.ok) {
        console.error('Failed to confirm tip:', confirmData.error);
        // Don't throw - payment was successful
      }

      // Show success confirmation screen
      handlePaymentSuccess({ amount: tipAmount, tip_percentage: selectedPercentage });

    } catch (error: any) {
      console.error('Terminal tip payment error:', error);
      setTerminalError(error.message || 'Failed to process tip payment');
      setTerminalStatus('');

      // Disconnect reader on error
      try {
        if (terminalRef.current) {
          const status = terminalRef.current.getConnectionStatus();
          if (status === 'connected') {
            await terminalRef.current.disconnectReader();
          }
        }
      } catch (disconnectErr) {
        // Silent cleanup
      }
    } finally {
      setIsProcessingTerminal(false);
      setCanCancelTerminal(false);
      paymentIntentRef.current = null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Add Tip</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        <div className="px-6 py-4">
          {step === 'success' && successData ? (
            /* Success Confirmation Screen */
            <div className="text-center py-6 space-y-6">
              {/* Green Checkmark Circle */}
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              {/* Success Message */}
              <div>
                <h3 className="text-2xl font-semibold text-gray-800">Tip Payment Successful!</h3>
                <p className="text-gray-600 mt-2">Thank you for your generosity</p>
              </div>

              {/* Tip Details */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm text-gray-600">Tip Amount</p>
                <p className="text-3xl font-bold text-green-700">
                  ${successData.amount.toFixed(2)}
                </p>
                {successData.tip_percentage && (
                  <p className="text-sm text-gray-500 mt-1">({successData.tip_percentage}%)</p>
                )}
                <p className="text-sm text-gray-600 mt-2">for {staffName}</p>
              </div>

              {/* Done Button */}
              <button
                type="button"
                onClick={handleSuccessDone}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium text-lg"
              >
                Done
              </button>
            </div>
          ) : step === 'select' ? (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-gray-600">Add a tip for {staffName}</p>
                <p className="text-lg font-semibold text-gray-800 mt-2">
                  Service Total: ${serviceTotal.toFixed(2)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Tip Percentage
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {presets.map((percentage) => (
                    <button
                      key={percentage}
                      type="button"
                      onClick={() => handlePresetClick(percentage)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedPercentage === percentage && !customPercentage
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-300 hover:border-green-400 text-gray-700'
                      }`}
                    >
                      <div className="font-semibold">{percentage}%</div>
                      <div className="text-xs mt-1">
                        ${((serviceTotal * percentage) / 100).toFixed(2)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or enter custom percentage
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={customPercentage}
                    onChange={(e) => handleCustomChange(e.target.value)}
                    placeholder="Enter percentage"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 pr-8"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>

              {tipAmount > 0 && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-600">Tip Amount</p>
                  <p className="text-2xl font-bold text-green-700">
                    ${tipAmount.toFixed(2)}
                  </p>
                </div>
              )}

              {errorMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
              )}

              {/* Payment Options */}
              {tipAmount > 0 && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Method
                  </label>
                  <div className="space-y-2">
                    {/* Saved Card Option */}
                    {(paymentMethodId || hasSavedCard) && (
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentOption('card')}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                          selectedPaymentOption === 'card'
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        <span className="text-xl">💳</span>
                        <div>
                          <div className="font-medium text-gray-900">Saved Card</div>
                          <div className="text-xs text-gray-500">Auto-charge to card on file</div>
                        </div>
                      </button>
                    )}

                    {/* Wallet Option */}
                    {walletBalance !== null && walletBalance > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentOption('wallet')}
                        disabled={walletBalance < tipAmount}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                          selectedPaymentOption === 'wallet'
                            ? 'border-green-600 bg-green-50'
                            : walletBalance < tipAmount
                              ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                              : 'border-gray-300 hover:border-green-400'
                        }`}
                      >
                        <span className="text-xl">👛</span>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">Pay from Wallet</div>
                          <div className="text-xs text-gray-500">
                            Balance: ${walletBalance.toFixed(2)}
                            {walletBalance < tipAmount && (
                              <span className="text-red-500 ml-1">(insufficient)</span>
                            )}
                          </div>
                        </div>
                      </button>
                    )}

                    {/* New Card Option */}
                    <button
                      type="button"
                      onClick={() => setSelectedPaymentOption('new_card')}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                        selectedPaymentOption === 'new_card'
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-300 hover:border-purple-400'
                      }`}
                    >
                      <span className="text-xl">➕</span>
                      <div>
                        <div className="font-medium text-gray-900">Enter Card</div>
                        <div className="text-xs text-gray-500">Pay with a different card</div>
                      </div>
                    </button>

                    {/* Terminal Option - for POS customers */}
                    <button
                      type="button"
                      onClick={() => setSelectedPaymentOption('terminal')}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                        selectedPaymentOption === 'terminal'
                          ? 'border-orange-600 bg-orange-50'
                          : 'border-gray-300 hover:border-orange-400'
                      }`}
                    >
                      <span className="text-xl">📱</span>
                      <div>
                        <div className="font-medium text-gray-900">Pay at Terminal</div>
                        <div className="text-xs text-gray-500">Tap card on S700 reader</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Terminal Payment Status */}
              {selectedPaymentOption === 'terminal' && (isProcessingTerminal || terminalStatus || terminalError) && (
                <div className="space-y-3">
                  {terminalStatus && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        {isProcessingTerminal && !terminalStatus.includes('successful') && (
                          <span className="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
                        )}
                        {terminalStatus.includes('successful') && (
                          <span className="text-green-600 text-xl">✓</span>
                        )}
                        <span className="text-blue-800 font-medium">{terminalStatus}</span>
                      </div>
                    </div>
                  )}
                  {terminalError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">{terminalError}</p>
                    </div>
                  )}
                  {canCancelTerminal && (
                    <button
                      type="button"
                      onClick={handleCancelTerminalPayment}
                      className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                    >
                      Cancel Payment
                    </button>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isProcessingTerminal}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  No Tip
                </button>
                {selectedPaymentOption === 'terminal' ? (
                  <button
                    type="button"
                    onClick={handleTerminalPayment}
                    disabled={selectedPercentage === null || tipAmount <= 0 || isProcessingTerminal}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessingTerminal ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Processing...
                      </span>
                    ) : (
                      'Pay at Terminal'
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={selectedPercentage === null || tipAmount <= 0 || isCreating}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreating
                      ? 'Processing...'
                      : selectedPaymentOption === 'wallet'
                        ? 'Pay from Wallet'
                        : selectedPaymentOption === 'card'
                          ? 'Confirm & Charge'
                          : 'Continue'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <TipPaymentForm
                  appointmentId={appointmentId}
                  tipPercentage={selectedPercentage}
                  tipAmount={tipAmount}
                  staffName={staffName}
                  onSuccess={handlePaymentSuccess}
                  onBack={() => setStep('select')}
                />
              </Elements>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default TipModal;
